import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { VoucherBook, useCreateVoucherBook, useUpdateVoucherBook } from '../hooks/useVoucherBooksApi';
import { Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editBook?: VoucherBook | null;
}

type FormData = {
  name: string;
  type: string;
};

const VOUCHER_TYPES = ['Contra', 'Credit Note', 'Debit Note', 'Purchase', 'Journal', 'Sales', 'Receipt', 'Payment'];

export function VoucherBookFormModal({ open, onOpenChange, editBook }: Props) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: '',
      type: 'Journal',
    }
  });

  const createBook = useCreateVoucherBook();
  const updateBook = useUpdateVoucherBook();
  
  const isSubmitting = createBook.isPending || updateBook.isPending;

  useEffect(() => {
    if (open) {
      if (editBook) {
        reset({
          name: editBook.name,
          type: editBook.type,
        });
      } else {
        reset({ name: '', type: 'Journal' });
      }
    }
  }, [open, editBook, reset]);

  const onSubmit = (data: FormData) => {
    if (editBook && editBook._id) {
      updateBook.mutate({ id: editBook._id, data }, {
        onSuccess: () => onOpenChange(false)
      });
    } else {
      createBook.mutate(data, {
        onSuccess: () => onOpenChange(false)
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{editBook ? 'Edit Voucher Book' : 'Create Voucher Book'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Voucher Book Name <span className="text-red-500">*</span></label>
            <Input 
              id="name" 
              placeholder="e.g. Sales Invoice Book" 
              {...register('name', { required: 'Name is required' })} 
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">Voucher Type <span className="text-red-500">*</span></label>
            <select 
              id="type"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
              {...register('type', { required: 'Type is required' })}
            >
              {VOUCHER_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {errors.type && <p className="text-xs text-red-500">{errors.type.message}</p>}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editBook ? 'Save Changes' : 'Create Book'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
